<template>
  <v-autocomplete
      :error-messages="error"
      variant="outlined"
      rounded="lg"
      bg-color="surface"
      :label="translation(translationList.house_building_height, 'Înălțime clădire')"
      :items="buildingHeights"
      item-title="name"
      item-value="id"
      name="building_height"
      :loading="loading"
      :disabled="loading"
      v-model="selectedBuildingHeight"
      :rules="[v => !!v || translation(translationList.required, 'Acest câmp este obligatoriu')]"
      return-object
  >
  </v-autocomplete>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, toRefs, watch } from 'vue';
import { useHouseStore } from '~/plugins/store/house';
import { useTranslationStore, translation } from '~/plugins/store/translations';
import type { BuildingHeight } from '~/types/House';
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    modelValue: {
      type: Object as () => BuildingHeight | null,
      default: () => null,
      required: false
    },
  },
  emits: ['update:modelValue', 'validation-house-status'],
  setup(props, { emit }) {
    const { modelValue } = toRefs(props);
    const houseStore = useHouseStore();
    const translationStore = useTranslationStore();
    const selectedBuildingHeight = ref(modelValue.value ? modelValue.value : null);
    const buildingHeights = ref<BuildingHeight[]>([]);
    const translationList = ref<TranslationList>({});
    const loading = ref(false);
    const error = ref<string[]>([]);

    onMounted(async () => {
      loading.value = true;
      buildingHeights.value = await houseStore.getBuildingHeightList();
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      loading.value = false;
      const isValid = !!selectedBuildingHeight.value;
      emit('validation-house-status', { field: 'building_height', isValid });

      // Listen for validate event
      window.addEventListener('validate-house', (data) => {
        if (data.detail && data.detail === 'building_height') {
          if (!selectedBuildingHeight.value) {
            error.value.push(translation(translationList.value.required, 'Acest câmp este obligatoriu'));
          } else {
            error.value = [];
            emit('validation-house-status', { field: 'building_height', isValid: true });
          }
        }
      });
    });

    watch(selectedBuildingHeight, (newValue) => {
      emit('update:modelValue', newValue);
    });

    watch(modelValue, (newValue) => {
      error.value = [];
      if (newValue && (newValue.id !== selectedBuildingHeight.value?.id)) {
        selectedBuildingHeight.value = newValue;
      }
      const isValid = !!newValue;
      emit('validation-house-status', { field: 'building_height', isValid });
    });

    return {
      selectedBuildingHeight,
      translationList,
      translation,
      buildingHeights,
      loading,
      error,
    };
  },
});
</script>